<template>
    <div class="form multiform">
        <form v-if="evt !== null && evt.hasOwnProperty('ticket_types')">
            <div class="mb-1">
                <header class="module-text-block wysiwyg">
                    <h2>Event Registration</h2>
                    <p>
                        Use the form below to complete your registration for
                        this event.
                    </p>
                    <p><strong>*</strong>Indicates required fields.</p>
                </header>
            </div>

            <!-- Panel - Your Event -------------------------------------------- -->
            <!-- if needed add v-slot:default="slotProps" -->
            <FormSection
                :sectionData="form.yourEvent"
                @advance="validateAndNext"
                @edit="selectSection"
            >
                <p class="event-info-p mb-0">
                    <span class="font-weight-600">{{ evt.title }}</span>
                </p>
                <p class="event-info-p mb-0">
                    <template v-if="evt.time">
                        <span class="font-weight-600">Time:</span> {{ evt.time }}
                    </template>
                    <span v-else>All Day</span>
                </p>
                <p class="event-info-p mb-1">
                    <span class="font-weight-600">Cost:</span>
                    <span v-if="evt.is_free">Free</span>
                    <span v-else>{{ evt.price_range }}</span>
                </p>
                <p class="event-info-p mb-1" v-if="evt.virtual">
                    Virtual
                    <span class="font-italic">
                        on {{ evt.virtual_type_text }}
                    </span>
                </p>

                <div class="form-row">
                    <div class="form-col is-1-2">
                        <label class="field select">
                            <span class="field-label">
                                Event Date<sup>*</sup>
                            </span>
                            <select
                                v-if="evt.occurences.length"
                                v-model="form.yourEvent.fields.event_date"
                                v-validate="'required'"
                                data-vv-as="Event Date"
                                name="event_date"
                                @change="updateCapacityForDate"
                            >
                                <option value="">Choose a Date</option>
                                <option
                                    v-for="(date, index) in evt.occurences"
                                    :value="date"
                                    :key="'date-' + index"
                                >
                                    {{ date }}
                                </option>
                            </select>
                            <div class="field-error">
                                {{ errors.first("event_date") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2">
                        <label class="field select">
                            <span class="field-label">
                                Ticket<sup>*</sup>
                            </span>
                            <select
                                v-model="form.yourEvent.fields.ticket_type_index"
                                v-validate="'required'"
                                data-vv-as="Ticket"
                                name="ticket_type_index"
                            >
                                <option :value="null">
                                {{
                                    form.yourEvent.fields.event_date == ''
                                    ? 'Finding Tickets ...'
                                    : desiredDateHasCapacity
                                        ? 'Choose a Ticket'
                                        : 'No Tickets Available'
                                }}
                                </option>
                                <template v-for="(type, index) in evt.ticket_types">
                                    <option
                                        v-if="(type.remaining == null || type.remaining > 0) && form.yourEvent.fields.event_date != ''"
                                        :value="index"
                                        :key="'type-' + index"
                                    >
                                        <template v-if="type.fee > 0">${{ type.fee }} - </template>
                                        {{ type.name }}
                                        <template v-if="type.fee <= 0"> (Free)</template>
                                    </option>
                                </template>
                            </select>
                            <div class="field-error">
                                {{ errors.first("ticket_type_index") }}
                            </div>
                        </label>
                    </div>
                </div>
            </FormSection>

            <!-- Panel - Your Information -------------------------------------------- -->
            <FormSection
                :sectionData="form.yourInfo"
                :showAdvanceButton="totalFee > 0"
                :showFinishButton="totalFee == 0"
                :isLocked="isProcessing"
                @advance="validateAndNext"
                @edit="selectSection"
                @finishForm="processFreeRegistration"
            >
                <div class="form-row">
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label"
                                >First Name<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.first_name"
                                v-validate="'required'"
                                data-vv-as="First Name"
                                name="first_name"
                                type="text"
                            />
                            <div class="field-error">
                                {{ errors.first("first_name") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label"
                                >Last Name<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.last_name"
                                v-validate="'required'"
                                data-vv-as="Last Name"
                                name="last_name"
                                type="text"
                            />
                            <div class="field-error">
                                {{ errors.first("last_name") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-1">
                        <label class="field text">
                            <span class="field-label"
                                >Street Address<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.street_address"
                                v-validate="'required'"
                                data-vv-as="Street Address"
                                name="street_address"
                                type="text"
                            />
                            <div class="field-error">
                                {{ errors.first("street_address") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label">City<sup>*</sup></span>
                            <input
                                v-model="form.yourInfo.fields.city"
                                v-validate="'required'"
                                data-vv-as="City"
                                name="city"
                                type="text"
                            />
                            <div class="field-error">
                                {{ errors.first("city") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2" v-if="USAstates != null">
                        <label class="field select">
                            <span class="field-label">State<sup>*</sup></span>
                            <select
                                v-model="form.yourInfo.fields.state"
                                v-validate="'required'"
                                data-vv-as="State"
                                name="state"
                            >
                                <option value="">Choose One</option>
                                <option
                                    v-for="(fullName,
                                    shortName,
                                    index) in USAstates"
                                    :value="shortName"
                                    :key="'state-' + index"
                                >
                                    {{ fullName }}
                                </option>
                            </select>
                            <div class="field-error">
                                {{ errors.first("state") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label"
                                >Zip Code<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.zip_code"
                                v-validate="'required'"
                                data-vv-as="Zip Code"
                                name="zip_code"
                                type="text"
                            />
                            <div class="field-error">
                                {{ errors.first("zip_code") }}
                            </div>
                        </label>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label"
                                >Email Address<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.email_address"
                                v-validate="'required|email'"
                                data-vv-as="Email"
                                name="email_address"
                                type="email"
                            />
                            <div class="field-error">
                                {{ errors.first("email_address") }}
                            </div>
                        </label>
                    </div>
                    <div class="form-col is-1-2">
                        <label class="field text">
                            <span class="field-label"
                                >Phone Number<sup>*</sup></span
                            >
                            <input
                                v-model="form.yourInfo.fields.phone_number"
                                v-validate="'required'"
                                name="phone_number"
                                type="tel"
                            />
                        </label>
                    </div>

                    <div
                        class="form-col is-1-2"
                        v-for="field in customFieldDefs"
                        :key="field.id"
                    >
                        <template v-if="field.type === 'text_field'">
                            <label class="field text">
                                <span class="field-label"
                                    >{{ field.label
                                    }}<sup v-if="field.required">*</sup></span
                                >
                                <input
                                    type="text"
                                    v-model="
                                        form.yourInfo.customFields[
                                            toSnakeCase(field.label)
                                        ]
                                    "
                                    v-validate="
                                        field.required ? 'required' : false
                                    "
                                    :name="toSnakeCase(field.label)"
                                    :key="field.id"
                                    :data-vv-as="field.label"
                                />
                                <div class="field-error">
                                    {{ errors.first(toSnakeCase(field.label)) }}
                                </div>
                            </label>
                        </template>

                        <template v-if="field.type === 'textarea_field'">
                            <label class="field textarea">
                                <span class="field-label"
                                    >{{ field.label
                                    }}<sup v-if="field.required">*</sup></span
                                >
                                <textarea
                                    v-model="
                                        form.yourInfo.customFields[
                                            toSnakeCase(field.label)
                                        ]
                                    "
                                    v-validate="
                                        field.required ? 'required' : false
                                    "
                                    :name="toSnakeCase(field.label)"
                                    :key="field.id"
                                    :data-vv-as="field.label"
                                >
                                </textarea>
                                <div class="field-error">
                                    {{ errors.first(toSnakeCase(field.label)) }}
                                </div>
                            </label>
                        </template>

                        <template v-if="field.type === 'dropdown_field'">
                            <label class="field select">
                                <span class="field-label"
                                    >{{ field.label
                                    }}<sup v-if="field.required">*</sup></span
                                >
                                <select
                                    v-model="
                                        form.yourInfo.customFields[
                                            toSnakeCase(field.label)
                                        ]
                                    "
                                    v-validate="
                                        field.required ? 'required' : false
                                    "
                                    :name="toSnakeCase(field.label)"
                                    :key="field.id"
                                    :data-vv-as="field.label"
                                >
                                    <option value="">Choose One</option>
                                    <option
                                        v-for="option in field.options"
                                        :value="option.value"
                                    >
                                        {{ option.value }}
                                    </option>
                                </select>
                                <div class="field-error">
                                    {{ errors.first(toSnakeCase(field.label)) }}
                                </div>
                            </label>
                        </template>

                        <template v-if="field.type === 'yes_no_field'">
                            <fieldset role="radiogroup">
                                <legend>
                                    <span class="field-label"
                                        >{{ field.label
                                        }}<sup v-if="field.required"
                                            >*</sup
                                        ></span
                                    >
                                </legend>
                                <div class="field">
                                    <label class="check">
                                        <input
                                            v-model="
                                                form.yourInfo.customFields[
                                                    toSnakeCase(field.label)
                                                ]
                                            "
                                            v-validate="
                                                field.required
                                                    ? 'required'
                                                    : false
                                            "
                                            :name="toSnakeCase(field.label)"
                                            :key="field.id"
                                            :data-vv-as="field.label"
                                            type="radio"
                                            class="check"
                                            value="y"
                                        />
                                        <span>Yes</span>
                                    </label>
                                </div>
                                <div class="field">
                                    <label class="check">
                                        <input
                                            v-model="
                                                form.yourInfo.customFields[
                                                    toSnakeCase(field.label)
                                                ]
                                            "
                                            :name="toSnakeCase(field.label)"
                                            :key="field.id"
                                            type="radio"
                                            class="check"
                                            value="n"
                                        />
                                        <span>No</span>
                                    </label>
                                </div>
                                <div class="field-error">
                                    {{ errors.first(toSnakeCase(field.label)) }}
                                </div>
                            </fieldset>
                        </template>

                        <template v-if="field.type === 'multi_select_field'">
                            <fieldset role="radiogroup">
                                <legend>
                                    <span class="field-label"
                                        >{{ field.label
                                        }}<sup v-if="field.required"
                                            >*</sup
                                        ></span
                                    >
                                </legend>
                                <div
                                    class="field"
                                    v-for="(option, i) in field.options"
                                    :key="option.value"
                                >
                                    <label class="check">
                                        <input
                                            v-if="i"
                                            type="checkbox"
                                            class="check"
                                            v-model="
                                                form.yourInfo.customFields[
                                                    toSnakeCase(field.label)
                                                ]
                                            "
                                            :name="toSnakeCase(field.label)"
                                            :data-vv-as="field.label"
                                            :false-value="[]"
                                            :value="option.value"
                                        />
                                        <input
                                            v-else
                                            type="checkbox"
                                            class="check"
                                            v-model="
                                                form.yourInfo.customFields[
                                                    toSnakeCase(field.label)
                                                ]
                                            "
                                            v-validate="
                                                field.required
                                                    ? 'required'
                                                    : false
                                            "
                                            :name="toSnakeCase(field.label)"
                                            :data-vv-as="field.label"
                                            :false-value="[]"
                                            :value="option.value"
                                        />
                                        <span>{{ option.value }}</span>
                                    </label>
                                </div>
                                <div class="field-error">
                                    {{ errors.first(toSnakeCase(field.label)) }}
                                </div>
                            </fieldset>
                        </template>
                    </div>
                </div>

                <div style="width: 0; height: 0; overflow: hidden;">
                    <textarea
                        name="custom_fields"
                        v-if="customFieldDefs.length"
                        v-text="customFieldsValue"
                    ></textarea>
                </div>

                <div v-if="desiredTicketHasCapacity === false">
                    <p class="mb-1">We&rsquo;re sorry, there are no more tickets of that type available for this date. Please <button class="restart" @click.prevent="goBackToDateSelect">choose a different date</button>.</p>
                </div>

            </FormSection>

            <!-- Panel - Your Payment -------------------------------------------- -->
            <FormSection
                v-if="totalFee > 0"
                :sectionData="form.yourPayment"
                :showAdvanceButton="false"
                :showFinishButton="false"
                :isLocked="isProcessing"
                @advance="validateAndNext"
                @edit="selectSection"
                @afterSectionOpen="loadPaymentFrame"
            >
                <div class="field-error" v-if="transactionError">
                    {{ transactionError }}
                </div>
                <div class="loading-wrap" v-if="!showPaymentFrame">
                    <div class="loading" v-if="desiredTicketHasCapacity === null">
                        <p>Loading ...</p>
                        <Loader />
                    </div>
                    <div class="loading" v-if="desiredTicketHasCapacity === false">
                        <p>We&rsquo;re sorry, there are no more tickets of that type available for this date. Please <button class="restart" @click.prevent="goBackToDateSelect">choose a different date</button>.</p>
                    </div>
                </div>
                <TCPaymentFrame
                    v-if="showPaymentFrame"
                    :transactionError="transactionError"
                    :successUrl="redirect"
                    :eventData="evt"
                    :formData="form"
                    :totalFee="totalFee"
                    :paymentUrl="paymentUrl"
                    :licenseKey="licenseKey"
                    :custId="custId"
                    @success="processPaidRegistration"
                />
            </FormSection>
        </form>

        <div class="form-error">
            <p v-show="formError != ''" v-html="formError"></p>
        </div>

        <!-- Debug Data -------------------------------------------- -->
        <div v-if="debug && this.evt !== null">
            <pre><code>ticket types: {{evt.ticket_types}}</code></pre>
        </div>

    </div>
</template>

<script>
import random from "../lib/random";
import snakeCaseHelper from "../lib/tosnakecase";
import FormSection from "./MultiFormSection";
import TCPaymentFrame from "./TCPaymentFrame";
import Loader from "./Loader";

export default {
    components: {
        FormSection: FormSection,
        TCPaymentFrame: TCPaymentFrame,
        Loader: Loader
    },
    data() {
        return {
            debug: false,
            evt: null,
            desiredDateHasCapacity: false,
            desiredTicketHasCapacity: null,
            action: null,
            redirect: null,
            paymentUrl: null,
            licenseKey: null,
            custId: null,
            form: {
                yourEvent: {
                    sectionName: null,
                    index: null,
                    heading: 'Your Event',
                    fields: {
                        event_date: '',
                        ticket_type_index: null
                    },
                    customFields: {},
                    isVisible: false,
                    isValid: false
                },
                yourInfo: {
                    sectionName: null,
                    index: null,
                    heading: "Your Information",
                    fields: {
                        first_name: "",
                        last_name: "",
                        street_address: "",
                        city: "",
                        state: "",
                        zip_code: "",
                        email_address: "",
                        phone_number: "",
                    },
                    customFields: {},
                    isVisible: false,
                    isValid: false
                },
                yourPayment: {
                    sectionName: null,
                    index: null,
                    heading: "Your Payment",
                    fields: {},
                    customFields: {},
                    isVisible: false,
                    isValid: false
                }
            },
            formError: '',
            customFieldDefs: [],
            customFieldsValue: '',
            totalFee: 0,
            transaction: {},
            transactionError: '',
            firstSection: null,
            activeSection: null,
            showPaymentFrame: false,
            isProcessing: false,
            USAstates: null
        };
    },
    watch: {
        fields: {
            deep: true,
            handler: function() {
                this.validateSection("yourEvent");
                this.validateSection("yourInfo");
            }
        },
        "form.yourInfo.customFields": {
            deep: true,
            handler: function() {
                this.$set(
                    this,
                    "customFieldsValue",
                    this.customFieldDefs
                        .map(field => {
                            const value = this.form.yourInfo.customFields[
                                this.toSnakeCase(field.label)
                            ];
                            return [
                                field.label,
                                field.type === "multi_select_field"
                                    ? value
                                        ? value.join(", ")
                                        : ""
                                    : value
                            ].join(": ");
                        })
                        .join("\n\n")
                );
            }
        },
        "form.yourEvent.fields.ticket_type_index": {
            handler: function(val) {
                let fee = 0;

                if (
                    val >= 0
                    && this.evt.ticket_types[val]
                ) {                
                    fee = parseFloat(this.evt.ticket_types[val].fee);
                }

                this.$set(this, "totalFee", Math.max(0, fee));
            }
        },
        "evt.ticket_types": {
            deep: true,
            handler: function(ticketTypes) {
                let availableTypes = [];
                let hasSome = false;
                let hasDesired = false;

                if (ticketTypes.length >= 1) {
                    ticketTypes.forEach(type => {
                        if(type.remaining !== 0) {
                            availableTypes.push(type.name);
                        }
                    });
                }
                
                if(availableTypes.length >= 1) {
                    hasSome = true;
                }

                this.$set(this, "desiredDateHasCapacity", hasSome);
            }
        }
    },
    computed: {
        /*
         * @note: VeeValidates adds its own computed properties:
         *
         * errors() {}
         * fields() {}
         */
        sectionIndexToName() {
            return Object.keys(this.form);
        }
    },
    mounted() {
        const customFields = {};

        // Get JSON data from Wagtail via <script> tags on the page
        let formData = document.getElementById("json-form-data");
        let eventData = document.getElementById("json-event-data");
        let customFieldsData = document.getElementById("json-custom-fields-data");
        let usaStatesData = document.getElementById("json-usa-states-data");

        // Set the data on this component
        let json = JSON.parse(formData.innerHTML);
        
        this.$set(this, "action", json.action);
        this.$set(this, "redirect", json.redirect);
        this.$set(this, "paymentUrl", json.paymentUrl);
        this.$set(this, "licenseKey", json.licenseKey);
        this.$set(this, "custId", json.custId);

        json = JSON.parse(eventData.innerHTML);

        this.$set(this, "evt", json);

        json = JSON.parse(customFieldsData.innerHTML);

        this.customFieldDefs = json;
        this.customFieldDefs.forEach(field => {
            customFields[this.toSnakeCase(field.label)] =
                field.type === "multi_select_field" ? [] : "";
        });
        this.$set(this.form.yourInfo, "customFields", customFields);

        json = JSON.parse(usaStatesData.innerHTML);

        this.USAstates = json;
        
        // Add a few more properties to each form section's data ("name" and "index")
        if (this.form) {
            Object.keys(this.form).forEach((key, index) => {
                if (index === 0) {
                    this.firstSection = key;
                }
                this.form[key].sectionName = key;
                this.form[key].index = index;
            });

            // Open the first step
            this.selectSection(this.firstSection);
        }

        /*
         * Debug code only, pre-fill our form
         * so we can test it faster
         */
        if (this.debug) {
            // this.form.yourEvent.fields.event_date = this.evt.occurences[0];
            this.form.yourInfo.fields.first_name = "Jane";
            this.form.yourInfo.fields.last_name = "Doe";
            this.form.yourInfo.fields.street_address = "123 Test St.";
            this.form.yourInfo.fields.city = "Somewhere";
            this.form.yourInfo.fields.state = "CA";
            this.form.yourInfo.fields.zip_code = "90001";
            this.form.yourInfo.fields.email_address = "engineering-logs@mostlyserious.io";
            this.form.yourInfo.fields.phone_number = "123-456-7890";

            // just working on the last step?
            // this.selectSection("yourInfo");
        }
    },
    methods: {
        getCSRF() {
            const input = document.querySelector(
                '[name="csrfmiddlewaretoken"]'
            );

            return input.getAttribute("value");
        },

        getTicketName() {
            let name = '';
            let index = this.form.yourEvent.fields.ticket_type_index;
            if(index >= 0 && this.evt.ticket_types[index]) {
                name = this.evt.ticket_types[index].name;
            }
            return name;
        },

        list(list, key) {
            return list
                .map(item => {
                    return item[key];
                })
                .filter(Boolean)
                .join(", ");
        },

        // sets the "isValid" property for a section
        validateSection(sectionName) {
            // this.fields is added by VeeValidate
            if (this.fields) {
                let invalidFieldNames = [];
                let sectionFieldNames = [
                    ...Object.keys(this.form[sectionName].fields),
                    ...Object.keys(this.form[sectionName].customFields)
                ];

                sectionFieldNames.forEach(name => {
                    if (this.fields[name] && this.fields[name].invalid) {
                        invalidFieldNames.push(name);
                    }
                });
                if (invalidFieldNames.length != 0) {
                    this.form[sectionName].isValid = false;
                } else {
                    this.form[sectionName].isValid = true;
                }
            }
        },

        // checks if a section is valid and opens the next one
        validateAndNext(sectionName) {
            this.validateSection(sectionName);
            if (this.form[sectionName].isValid) {
                let nextSectionIndex = this.form[sectionName].index + 1;
                this.selectSectionByIndex(nextSectionIndex);
            }
        },

        // sets the current active section, and closes the others
        selectSection(sectionName) {
            this.activeSection = sectionName;
            Object.keys(this.form).forEach(key => {
                this.form[key].isVisible = sectionName === key;
            });
        },

        // sets the current active section using its 0 based index instead of its name
        selectSectionByIndex(index) {
            this.selectSection(this.sectionIndexToName[index]);
        },

        getCapacity () {
            let payload = {
                event: this.evt.id,
                event_date: this.form.yourEvent.fields.event_date,
            };
            
            return fetch('/events/register/check-capacity/', {
                body: new URLSearchParams(payload),
                method: "POST",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: { "X-CSRFToken": this.getCSRF() },
                redirect: "follow"
            });
        },

        updateCapacityForDate () {
            if (this.form.yourEvent.fields.event_date) {
                this.form.yourEvent.fields.ticket_type_index = null;
                this.getCapacity()
                    .then(result => {
                        return result.json();
                    })
                    .then(response => {
                        if (response.ticket_types.length) {
                            response.ticket_types.forEach((item, index) => {
                                this.evt.ticket_types[index].capacity = item['capacity'];
                                this.evt.ticket_types[index].filled = item['filled'];
                                this.evt.ticket_types[index].remaining = item['remaining'];
                            });
                        }
                    })
                    .catch(error => {
                        console.error("Error:", error);
                    });
            }
        },

        loadPaymentFrame () {
            this.showPaymentFrame = false;
            this.getCapacity()
                .then(result => {
                    return result.json();
                })
                .then(response => {
                    let name = this.getTicketName();
                    if (
                        response.available_types.length
                        && response.available_types.includes(name)
                    ) {
                        this.showPaymentFrame = true;
                    } else {
                        this.desiredTicketHasCapacity = false;
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                    this.showPaymentFrame = false;
                });
        },

        // No TrustCommerce response required
        processFreeRegistration () {
            this.isProcessing = true;

            this.$validator.validateAll().then((isValid) => {
                if (isValid) {
                    this.getCapacity()
                        .then(result => {
                            return result.json();
                        })
                        .then(response => {
                            let name = this.getTicketName();
                            if (response.available_types.length && response.available_types.includes(name)) {
                                this.saveRegistration();
                            } else {
                                this.isProcessing = false;
                                this.desiredTicketHasCapacity = false;
                            }
                        })
                        .catch(error => {
                            console.error("Error:", error);
                            this.isProcessing = false;
                        });
                } else {
                    this.isProcessing = false;
                    this.formError = 'Your form contains some invalid fields. Please review each section.'
                }
            });
        },

        // TrustCommerce transaction occurred in iframe
        processPaidRegistration(successData) {
            this.isProcessing = true;
            this.transaction = successData;

            if (this.transaction["transaction[status_name]"] === 'approved') {
                this.saveRegistration();
            } else {
                this.transactionError =
                    'Transaction was declined. Please confirm that your credit card credentials are correct and that your card is not expired.';
            }
        },

        saveRegistration() {
            const data = {
                title: this.transaction["transaction[ticket]"]
                    ? this.transaction["transaction[ticket]"]
                    : random(),
                transaction_id: this.transaction["transaction[transid]"]
                    ? this.transaction["transaction[transid]"]
                    : "N/A",
                transaction_amount: this.transaction["transaction[amount]"]
                    ? this.transaction["transaction[amount]"]
                    : "N/A",
                event_date: this.form.yourEvent.fields.event_date,
                ticket_type: this.getTicketName(),
                first_name: this.form.yourInfo.fields.first_name,
                last_name: this.form.yourInfo.fields.last_name,
                event: this.evt.id,
                department_name: this.list(this.evt.departments, "title"),
                department_number: this.list(this.evt.departments, "number"),
                department_revenue_account: this.list(
                    this.evt.departments,
                    "revenueAccount"
                ),
                department_contact: this.list(this.evt.departments, "contact"),
                event_activity: this.evt.eventActivity,
                event_category: this.evt.eventCategory,
                street_address: this.form.yourInfo.fields.street_address,
                city: this.form.yourInfo.fields.city,
                state: this.form.yourInfo.fields.state,
                zip_code: this.form.yourInfo.fields.zip_code,
                email_address: this.form.yourInfo.fields.email_address,
                phone_number: this.form.yourInfo.fields.phone_number,
                custom_fields: this.customFieldsValue
            };

            fetch(this.action, {
                body: new URLSearchParams(data),
                method: "POST",
                mode: "cors",
                cache: "no-cache",
                credentials: "same-origin",
                headers: { "X-CSRFToken": this.getCSRF() },
                redirect: "follow"
            })
                .then(res => {
                    return res.json();
                })
                .then(data => {
                    if (data.success) {
                        window.location = [
                            window.location.protocol,
                            "//",
                            window.location.host,
                            "/events/register/thank-you/?id=",
                            data.id
                        ].join("");
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                    this.formError = 'An unexpected error occurred. We can not complete your registration at this time.'
                });
        },

        goBackToDateSelect () {
            this.formError = '';
            this.desiredTicketHasCapacity = null;
            this.selectSection('yourEvent');
            setTimeout(() => {
                this.form.yourEvent.fields.event_date = '';
                this.form.yourEvent.fields.ticket_type_index = null;
            }, 2000);            
        },

        toSnakeCase: snakeCaseHelper
    }
};
</script>

<style scoped>
.form-error {
    padding: 2rem 0 2rem 0;
}
.form-error p{
    padding: 1rem;
    color: #CF4520;
}
.loading-wrap {
    padding-bottom: 2rem;
    min-height: 450px;
}
.loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    justify-items: center;
    background: #eee;
    border-radius: 4px;
    color: #777;
    min-height: 450px;
}
.loading p {
    margin-bottom: 0.5em;
}
button.restart {
    font-weight: bold;
    text-decoration: underline;
    cursor: pointer;
}
button.restart:hover {
    text-decoration: none;
}
</style>
